import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import PublicHeader from '../../components/PublicHeader';
import PublicFooter from '../../components/PublicFooter';
import PublicCouponBanner from '../../components/PublicCouponBanner';
import '../../pcos-management-style.css';
import { apiGet, apiPost } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useCountry } from '../../context/CountryContext';
import { useNotification } from '../../context/CustomDialogContext';
import { useLeadModal } from '../../context/LeadModalContext';
import CountryPhoneInput from '../../components/CountryPhoneInput';
import CourseEnrollModal from '../../components/CourseEnrollModal';
import courseBgImage from '../../assets/images/courses/course_bg.webp';
import heroCourseImage from '../../assets/images/courses/500hr-yttc.webp';

/* ─── Accordion Item (Syllabus & FAQ) ─────────────────────────────────────── */
const AccordionItem = ({ num, title, subtitle, children, isActive, onToggle }) => {
  const contentRef = useRef(null);
  const [height, setHeight] = useState('0px');

  useEffect(() => {
    setHeight(isActive ? `${contentRef.current ? contentRef.current.scrollHeight : 500}px` : '0px');
  }, [isActive]);

  return (
    <div className={`fy-accordion-item ${isActive ? 'active' : ''}`}>
      <button
        className="fy-accordion-btn"
        onClick={onToggle}
        aria-expanded={isActive}
        type="button"
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: '12px', minWidth: 0 }}>
          {num && <span className="fy-accordion-num">{num}</span>}
          <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
            <span style={{ fontWeight: 700 }}>{title}</span>
            {subtitle && <span className="fy-accordion-subtitle">{subtitle}</span>}
          </span>
        </span>
        <div className="fy-accordion-icon"><i className="bi bi-plus"></i></div>
      </button>
      <div className="fy-accordion-content" style={{ maxHeight: height }}>
        <div ref={contentRef} className="fy-accordion-inner">
          {children}
        </div>
      </div>
    </div>
  );
};

/* ─── Main Component ──────────────────────────────────────────────────────── */
const YogaTTC500 = () => {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { selectedCountry, formatPrice } = useCountry();
  const { showAlert } = useNotification();
  const { openModal } = useLeadModal();

  const [isEnrollModalOpen, setIsEnrollModalOpen] = useState(false);

  const courseTitle = "500 Hour Yoga Teacher Training";

  const faqs = [
    { q: "Who can join this 500-hour course?", a: "This course is designed for dedicated yoga practitioners, existing teachers, and wellness professionals who want a comprehensive, high-level master training experience." },
    { q: "Do I need a 200-hour certificate before joining?", a: "Prior yoga experience is required. If you wish to build a master-level foundation, this 500-hour course provides complete end-to-end training." },
    { q: "How is the course delivered?", a: "It is delivered live online in daily 1-hour sessions, with recordings uploaded to your private PranaFlow Student Dashboard after class." },
    { q: "What happens if I miss a live class?", a: "All live classes are recorded and uploaded to your dashboard on the same day so you can stay fully aligned with your batch." },
    { q: "Can I rewatch lectures I have already attended?", a: "Yes. All recorded sessions remain available on your dashboard for revision during your access period." },
    { q: "Who issues the certificate?", a: "Your official 500-Hour Yoga Teacher Certificate is issued through one of PranaFlow Academy’s verified Yoga Alliance-registered partner schools." },
    { q: "How many students are in each batch?", a: "Each batch is strictly limited to a maximum of 25 students for personalized attention." },
    { q: "Can I pay tuition in installments?", a: "Yes, flexible installment payment options are available according to configured course plans." },
    { q: "Does this course cover yoga philosophy?", a: "Yes! 5 modules are dedicated to Patanjali's Yoga Sutras, Bhagavad Gita, 8 Limbs, Mantra, and Yogic Lifestyle." },
    { q: "Will I receive a Student Dashboard?", a: "Yes, after successful enrollment according to the standard PranaFlow Academy LMS account flow." }
  ];

  const getLogoUrl = () => {
    const apiBase = import.meta.env.VITE_API_BASE_URL || '/my-lms/backend/api';
    return `${apiBase}/logo.php`;
  };

  const [activeModule, setActiveModule] = useState(0);
  const [activeFaq, setActiveFaq] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    father_name: '',
    address: '',
    countryCode: '+91',
    phone: '',
    email: '',
    batch_id: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const [dbCourse, setDbCourse] = useState(null);
  const [demoInfo, setDemoInfo] = useState(null);
  const [upcomingBatches, setUpcomingBatches] = useState([]);
  const [loadingBatches, setLoadingBatches] = useState(true);
  const [activeCoupons, setActiveCoupons] = useState([]);
  const [currentTime, setCurrentTime] = useState(new Date());

  /* Fetch active coupons */
  useEffect(() => {
    async function fetchCoupons() {
      try {
        let couponsRes = await apiGet('/public-banner-coupons.php');
        if (!couponsRes || (Array.isArray(couponsRes) && couponsRes.length === 0) || (couponsRes.data && couponsRes.data.length === 0)) {
          couponsRes = await apiGet('/public-coupons.php');
        }
        let list = [];
        if (Array.isArray(couponsRes)) {
          list = couponsRes;
        } else if (couponsRes && Array.isArray(couponsRes.data)) {
          list = couponsRes.data;
        } else if (couponsRes && Array.isArray(couponsRes.list)) {
          list = couponsRes.list;
        }
        setActiveCoupons(list);
      } catch (err) {
        console.error('Failed to fetch coupons:', err);
      }
    }
    fetchCoupons();
  }, []);

  /* 1-second interval for real-time coupon countdown */
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const parseDateSafe = (dateStr) => {
    if (!dateStr) return null;
    let cleanStr = String(dateStr).trim();
    if (!cleanStr) return null;

    let d = new Date(cleanStr);
    if (!isNaN(d.getTime())) return d;

    cleanStr = cleanStr.replace('T', ' ').replace(/-/g, '/');
    d = new Date(cleanStr);
    if (!isNaN(d.getTime())) return d;

    d = new Date(cleanStr.replace(/ /g, 'T'));
    if (!isNaN(d.getTime())) return d;

    return null;
  };

  const getBannerCouponFor500Hour = () => {
    if (!dbCourse) return null;

    const match = activeCoupons.find(
      (c) => Number(c.course_id) === Number(dbCourse.id) || Number(c.course_id) === 0
    );
    if (!match) return null;

    if (match.status && match.status !== 'active') return null;

    const isShowOnPages = match.show_on_pages === undefined || match.show_on_pages === null || match.show_on_pages === 1 || match.show_on_pages === '1' || match.show_on_pages === true;
    if (!isShowOnPages) return null;

    const now = currentTime.getTime();

    if (match.banner_expiry) {
      const expDate = parseDateSafe(match.banner_expiry);
      if (expDate && expDate.getTime() - now <= 0) return null;
    }
    if (match.banner_start) {
      const startDate = parseDateSafe(match.banner_start);
      if (startDate && now < startDate.getTime()) return null;
    }
    if (match.expiry_date) {
      const expDate = parseDateSafe(match.expiry_date);
      if (expDate && expDate.getTime() - now <= 0) return null;
    }

    return match;
  };

  const formatCountdown = (expiryDateStr) => {
    const expDate = parseDateSafe(expiryDateStr);
    if (!expDate) return '';

    const difference = expDate.getTime() - currentTime.getTime();
    if (difference <= 0) return 'Expired';

    const days = Math.floor(difference / (1000 * 60 * 60 * 24));
    const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((difference % (1000 * 60)) / 1000);

    const parts = [];
    if (days > 0) {
      parts.push(`${days} day${days > 1 ? 's' : ''}`);
    }
    parts.push(`${hours} hr`);
    parts.push(`${minutes} min`);
    parts.push(`${seconds} sec`);

    return parts.join(' ');
  };

  /* Scoped CSS class for design system */
  useLayoutEffect(() => {
    document.body.classList.add('pcos-page');
    return () => { document.body.classList.remove('pcos-page'); };
  }, []);

  /* Load 500 Hour course data & batches & demo availability */
  useEffect(() => {
    async function loadData() {
      try {
        const coursesData = await apiGet('/public-courses.php');
        let currentCourseId = 0;
        let targetTitle = courseTitle;
        if (coursesData && Array.isArray(coursesData)) {
          let match = coursesData.find(
            c => c.title && c.title.toLowerCase().trim() === courseTitle.toLowerCase().trim()
          );
          if (!match) {
            match = coursesData.find(
              c => (c.slug && c.slug.toLowerCase().trim() === '500hr-yoga-ttc') ||
                   (c.course_slug && c.course_slug.toLowerCase().trim() === '500hr-yoga-ttc') ||
                   (c.title && c.title.toLowerCase().includes('500 hour')) ||
                   (c.title && c.title.toLowerCase().includes('500hr'))
            );
          }
          if (match) {
            setDbCourse(match);
            currentCourseId = match.id;
            if (match.title) {
              targetTitle = match.title;
            }
          }
        }

        // Query upcoming batches for 500 Hour YTTC
        let batchesData = await apiGet(`/get-upcoming-batches.php?course_title=${encodeURIComponent(targetTitle)}`);
        
        if ((!batchesData || !Array.isArray(batchesData) || batchesData.length === 0) && targetTitle !== courseTitle) {
          const fallbackBatches = await apiGet(`/get-upcoming-batches.php?course_title=${encodeURIComponent(courseTitle)}`);
          if (fallbackBatches && Array.isArray(fallbackBatches) && fallbackBatches.length > 0) {
            batchesData = fallbackBatches;
          }
        }

        if ((!batchesData || !Array.isArray(batchesData) || batchesData.length === 0) && currentCourseId > 0) {
          const idBatches = await apiGet(`/get-upcoming-batches.php?course_id=${currentCourseId}`);
          if (idBatches && Array.isArray(idBatches) && idBatches.length > 0) {
            batchesData = idBatches;
          }
        }

        setUpcomingBatches(batchesData || []);

        try {
          if (currentCourseId > 0) {
            const demoRes = await apiGet(`/public/check-demo-course.php?course_id=${currentCourseId}&slug=500hr-yoga-ttc`);
            if (demoRes && demoRes.available) {
              setDemoInfo(demoRes);
            }
          }
        } catch (err) {
          console.log("No demo configuration for this course.");
        }
      } catch (err) {
        console.error('Failed to load course details & batches:', err);
      } finally {
        setLoadingBatches(false);
      }
    }
    loadData();
  }, []);

  /* Sync logged-in student data */
  useEffect(() => {
    if (user && user.role === 'student') {
      setFormData(prev => ({
        ...prev,
        name: user.name || '',
        email: user.email || '',
        countryCode: user.country_code || '+91',
        phone: user.mobile_number || user.phone || '',
        father_name: user.father_name || '',
        address: user.address || ''
      }));
    }
  }, [user]);

  /* SEO & smooth scroll */
  useEffect(() => {
    document.title = "500 Hour Yoga Teacher Training Online Rishikesh | PranaFlow Academy";
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute('content',
        "Join PranaFlow Academy's live online 500 Hour Yoga Teacher Training from Rishikesh. Master-level curriculum, expert partner teachers, batches of 25. Enroll now.");
    }

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', "https://pranaflowacademy.com/courses/500hr-yoga-ttc");

    // Open Graph Metadata
    const ogTags = [
      { property: 'og:title', content: "500 Hour Yoga Teacher Training Online Rishikesh | PranaFlow Academy" },
      { property: 'og:description', content: "Join PranaFlow Academy's live online 500 Hour Yoga Teacher Training from Rishikesh. Master-level curriculum, expert partner teachers, batches of 25. Enroll now." },
      { property: 'og:type', content: "website" },
      { property: 'og:url', content: "https://pranaflowacademy.com/courses/500hr-yoga-ttc" },
      { property: 'og:image', content: "https://pranaflowacademy.com/images/courses/500hr-yttc.webp" },
      { property: 'og:site_name', content: "PranaFlow Academy" }
    ];

    ogTags.forEach(tag => {
      let el = document.querySelector(`meta[property="${tag.property}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('property', tag.property);
        document.head.appendChild(el);
      }
      el.setAttribute('content', tag.content);
    });

    // Twitter / X Metadata
    const twitterTags = [
      { name: 'twitter:card', content: "summary_large_image" },
      { name: 'twitter:title', content: "500 Hour Yoga Teacher Training Online Rishikesh | PranaFlow Academy" },
      { name: 'twitter:description', content: "Join PranaFlow Academy's live online 500 Hour Yoga Teacher Training from Rishikesh. Master-level curriculum, expert partner teachers, batches of 25. Enroll now." },
      { name: 'twitter:image', content: "https://pranaflowacademy.com/images/courses/500hr-yttc.webp" }
    ];

    twitterTags.forEach(tag => {
      let el = document.querySelector(`meta[name="${tag.name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('name', tag.name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', tag.content);
    });

    if (location.hash) {
      const id = location.hash.replace('#', '');
      const el = document.getElementById(id);
      if (el) setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 300);
    } else {
      window.scrollTo(0, 0);
    }
  }, [location.hash, location.pathname]);

  /* Schema injection */
  useEffect(() => {
    const scriptId = 'jsonld-schema-500h';
    let script = document.getElementById(scriptId);
    if (!script) {
      script = document.createElement('script');
      script.id = scriptId;
      script.type = 'application/ld+json';
      document.head.appendChild(script);
    }
    const courseSchema = {
      "@context": "https://schema.org",
      "@type": "Course",
      "@id": "https://pranaflowacademy.com/courses/500hr-yoga-ttc#course",
      "name": "500 Hour Yoga Teacher Training Course",
      "description": "PranaFlow Academy's 500 Hour Yoga Teacher Training Course is a comprehensive yoga education program for students who want deeper study, advanced practice, teaching methodology, philosophy, and professional yoga training.",
      "url": "https://pranaflowacademy.com/courses/500hr-yoga-ttc",
      "provider": {
        "@id": "https://pranaflowacademy.com/#organization"
      },
      "courseMode": "online",
      "educationalCredentialAwarded": "Certificate of Completion",
      "inLanguage": "en"
    };

    const breadcrumbSchema = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Home",
          "item": "https://pranaflowacademy.com/"
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "Courses",
          "item": "https://pranaflowacademy.com/courses"
        },
        {
          "@type": "ListItem",
          "position": 3,
          "name": "500 Hour Yoga Teacher Training Course",
          "item": "https://pranaflowacademy.com/courses/500hr-yoga-ttc"
        }
      ]
    };

    const faqPageSchema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": faqs.map(item => ({
        "@type": "Question",
        "name": item.q,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": item.a
        }
      }))
    };

    script.textContent = JSON.stringify([courseSchema, breadcrumbSchema, faqPageSchema]);

    return () => {
      const existingScript = document.getElementById(scriptId);
      if (existingScript) existingScript.remove();
    };
  }, []);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const displayPrice = dbCourse && parseFloat(dbCourse.fee_inr) > 0 ? formatPrice(dbCourse.fee_inr, dbCourse) : (dbCourse ? 'Fee will be updated soon' : 'Loading...');

  const handleEnrollSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) { showAlert({ title: "Warning", message: "Student Name is required.", type: "warning" }); return; }
    if (!formData.email.trim()) { showAlert({ title: "Warning", message: "Email address is required.", type: "warning" }); return; }
    if (!formData.countryCode.trim()) { showAlert({ title: "Warning", message: "Country Code is required.", type: "warning" }); return; }
    if (!formData.phone.trim()) { showAlert({ title: "Warning", message: "Please enter your mobile number.", type: "warning" }); return; }

    const isInactive = dbCourse && dbCourse.status === 'inactive';

    if (isInactive) {
      setIsSubmitting(true);
      try {
        const payload = {
          name: formData.name.trim(),
          father_name: formData.father_name ? formData.father_name.trim() : null,
          mobile: formData.phone.trim(),
          country_code: formData.countryCode.trim(),
          mobile_number: formData.phone.trim(),
          country_iso_code: selectedCountry ? selectedCountry.country_code : 'IN',
          email: formData.email.trim(),
          address: formData.address ? formData.address.trim() : null,
          course_id: dbCourse ? dbCourse.id : null,
          country: selectedCountry ? selectedCountry.country_name : null,
          message: "I am interested in this course. Please contact me when new batches are available.",
          source_page: window.location.pathname
        };

        await apiPost('/save-interested-lead.php', payload);
        setIsSuccess(true);
      } catch (err) {
        showAlert({ title: "Error", message: err.message || "Failed to submit details. Please try again.", type: "error" });
      } finally {
        setIsSubmitting(false);
      }
      return;
    }

    if (!formData.father_name.trim()) { showAlert({ title: "Warning", message: "Father's / Guardian's Name is required.", type: "warning" }); return; }
    if (!formData.address.trim()) { showAlert({ title: "Warning", message: "Full Address is required.", type: "warning" }); return; }
    if (!formData.batch_id) { showAlert({ title: "Warning", message: "Please select an upcoming batch.", type: "warning" }); return; }

    const selectedBatch = upcomingBatches.find(b => Number(b.id) === Number(formData.batch_id));
    if (!selectedBatch) { showAlert({ title: "Warning", message: "Selected batch not found.", type: "warning" }); return; }

    navigate('/enroll-confirm', {
      state: {
        studentDetails: {
          name: formData.name,
          father_name: formData.father_name,
          address: formData.address,
          countryCode: formData.countryCode,
          phone: formData.phone,
          email: formData.email
        },
        courseDetails: {
          id: dbCourse ? dbCourse.id : null,
          title: dbCourse ? dbCourse.title : courseTitle,
          duration: dbCourse ? dbCourse.duration : '500 Days',
          fee_inr: dbCourse ? dbCourse.fee_inr : 0,
          fee_usd: dbCourse ? dbCourse.fee_usd : 0
        },
        batchDetails: {
          id: selectedBatch.id,
          name: selectedBatch.batch_name,
          timing: selectedBatch.batch_timing,
          start_date: selectedBatch.start_date
        }
      }
    });
  };

  /* 18 Modules Syllabus Data */
  const modules = [
    {
      num: '01',
      title: 'Foundations, History & Evolution of Yoga',
      desc: 'Explore the origin, Vedic lineage, classical traditions, historical shifts, and modern evolution of yoga as a transformative life science.'
    },
    {
      num: '02',
      title: 'Yoga Philosophy & the Eight Limbs',
      desc: "In-depth study of Patanjali's Yoga Sutras, Yamas, Niyamas, Asana, Pranayama, Pratyahara, Dharana, Dhyana, and Samadhi for daily life and teaching."
    },
    {
      num: '03',
      title: 'Bhagavad Gita & Yogic Living',
      desc: 'Understand Karma Yoga, Bhakti Yoga, Jnana Yoga, and Raja Yoga principles from the Bhagavad Gita to build psychological steadiness and yogic ethics.'
    },
    {
      num: '04',
      title: 'Hatha Yoga Asana Foundations',
      desc: 'Master classical Hatha postures, muscular alignment, joint stabilization, weight distribution, and energetic grounding.'
    },
    {
      num: '05',
      title: 'Advanced Asana, Alignment & Progression',
      desc: 'Progress safely into advanced inversions, deep backbends, arm balances, posture modifications, and injury prevention.'
    },
    {
      num: '06',
      title: 'Functional Anatomy for Yoga Teachers',
      desc: 'Study skeletal structure, muscular origin/insertion, joint biomechanics, spine movement, and movement mechanics during yoga practice.'
    },
    {
      num: '07',
      title: 'Physiology, Movement & Injury Awareness',
      desc: 'Understand the nervous system, endocrine system, respiratory system, circulatory system, fascia networks, and safe posture adaptations.'
    },
    {
      num: '08',
      title: 'Pranayama & Breath Education',
      desc: 'Master vital breathing techniques (Nadi Shodhana, Kapalabhati, Bhastrika, Ujjayi, Sheetali) and breath retention (Kumbhaka) principles.'
    },
    {
      num: '09',
      title: 'Meditation, Concentration & Mindfulness',
      desc: 'Practice Dharana and Dhyana techniques, Nidra guided relaxation, mindfulness observation, and mental quietude.'
    },
    {
      num: '10',
      title: 'Mudras, Bandhas & Subtle Body Concepts',
      desc: 'Explore Mula, Uddiyana, and Jalandhara Bandhas, energy channels (Nadis), Chakras, Vayus, and subtle energy direction.'
    },
    {
      num: '11',
      title: 'Mantra, Chanting & Yogic Sound',
      desc: 'Study Vedic chanting, Sanskrit pronunciation, Bija mantras, resonance, and the meditative impact of sound vibration.'
    },
    {
      num: '12',
      title: 'Yoga Sequencing & Class Architecture',
      desc: 'Learn how to design intelligent, balanced, goal-oriented class sequences for peak postures, restorative sessions, and thematic flows.'
    },
    {
      num: '13',
      title: 'Teaching Methodology',
      desc: 'Refine your verbal cueing, voice modulation, observation skills, class pacing, demonstration techniques, and presence.'
    },
    {
      num: '14',
      title: 'Adaptation, Accessibility & Individual Differences',
      desc: 'Learn props usage, chair yoga adaptations, age-specific modifications, and inclusive teaching for diverse student needs.'
    },
    {
      num: '15',
      title: 'Ayurveda & Yogic Lifestyle',
      desc: 'Understand Tridosha (Vata, Pitta, Kapha), Ahara (nutrition), Dinacharya (daily routine), and seasonal wellness practices.'
    },
    {
      num: '16',
      title: 'Yoga Ethics & Professional Responsibility',
      desc: 'Maintain student-teacher boundaries, ethics, inclusivity, professional standards, and authentic yogic guidance.'
    },
    {
      num: '17',
      title: 'Yoga Teaching Practicum',
      desc: 'Deliver live teaching demonstrations, lead group practice sessions, receive structured instructor feedback, and refine your teaching voice.'
    },
    {
      num: '18',
      title: 'Final Integration, Assessment & Certification',
      desc: 'Complete practical evaluations, case study assignments, final assessment, and earn your official PranaFlow Academy 500-Hour Certificate.'
    }
  ];

  /* 8 Core Pillars */
  const pillars = [
    {
      num: '01',
      title: 'Deepen Your Personal Practice',
      tags: ['Advanced Asana', 'Posture Mastery', 'Alignment'],
      desc: 'Master advanced postures, physical alignment, stability, and posture progressions through daily guided live practice.'
    },
    {
      num: '02',
      title: 'Understand the Body',
      tags: ['Functional Anatomy', 'Biomechanics', 'Injury Prevention'],
      desc: 'Study bone mechanics, muscle origin/insertion, joint movement, fascia, and safe injury-prevention principles.'
    },
    {
      num: '03',
      title: 'Study Philosophy Beyond Physical Practice',
      tags: ['Yoga Sutras', 'Bhagavad Gita', '8 Limbs of Yoga'],
      desc: 'Explore Patanjali’s Yoga Sutras, Bhagavad Gita wisdom, ethical Yamas & Niyamas, and yogic mind science.'
    },
    {
      num: '04',
      title: 'Understand Breath & Pranayama',
      tags: ['Kumbhaka', 'Nadi Shodhana', 'Energy Vitality'],
      desc: 'Master deep breathing techniques, pranayama mechanics, breath retention, and autonomic nervous system regulation.'
    },
    {
      num: '05',
      title: 'Learn How to Build Classes',
      tags: ['Peak Posture Design', 'Sequencing Logic', 'Pacing'],
      desc: 'Design intelligent, balanced class architectures for Hatha, Vinyasa, restorative, and thematic sequence goals.'
    },
    {
      num: '06',
      title: 'Learn to Teach Different People',
      tags: ['Props Adaptation', 'Modifications', 'Inclusive Teaching'],
      desc: 'Adapt postures for beginner, intermediate, senior, and stiff bodies using props, variations, and clear modifications.'
    },
    {
      num: '07',
      title: 'Develop Your Teaching Voice',
      tags: ['Verbal Cues', 'Class Pacing', 'Teacher Presence'],
      desc: 'Refine articulate verbal direction, posture observation, tone modulation, and calm teacher confidence.'
    },
    {
      num: '08',
      title: 'Practise Becoming the Teacher',
      tags: ['Teaching Practicum', 'Instructor Feedback', 'Certification'],
      desc: 'Lead real practice teaching sessions, receive peer and mentor feedback, and complete your 500-hour certification.'
    }
  ];

  /* Common Learning Barriers */
  const challengePoints = [
    { title: "Surface-level physical practice", desc: "Practising postures for years without understanding subtle alignment, breath, or mind connection" },
    { title: "Confusion about classical philosophy", desc: "Reading books on Yoga Sutras or Gita without knowing how to apply them to daily life or teaching" },
    { title: "Fear of injury in advanced poses", desc: "Uncertain how to safely enter or modify inversions, arm balances, and deep backbends" },
    { title: "Unstructured class sequencing", desc: "Randomly putting postures together without clear anatomical or energetic class flow logic" },
    { title: "Lack of anatomical understanding", desc: "Teaching Cues without knowing joint limitations, origin/insertion, or individual structural differences" },
    { title: "Weak verbal cueing & presence", desc: "Struggling to command attention, give clear alignment cues, or project a steady teacher voice" },
    { title: "Inability to adapt for beginners or injury", desc: "Not knowing how to modify poses for students with tight hamstrings, wrist pain, or back sensitivity" },
    { title: "Superficial breathwork knowledge", desc: "Teaching basic pranayama without understanding breath mechanics, Kumbhaka, or energy effects" },
    { title: "Disconnected meditation guidance", desc: "Difficulty leading quiet concentration, mindfulness, or guided relaxation with confidence" },
    { title: "Isolation in self-guided learning", desc: "Watching random video clips online without structured mentor feedback or batch interaction" },
    { title: "Lack of real teaching practicum", desc: "Completing theory without delivering actual live classes and receiving constructive critique" },
    { title: "Needing a comprehensive 500-hour credential", desc: "Seeking a complete 500-hour master certificate to establish professional teaching credibility" }
  ];

  const highlights = [
    "500 Hours of Live Interactive Master-Level Instruction",
    "Completed Across 500 Training Days (~1 Hour Daily)",
    "Master 18 Comprehensive Educational Modules",
    "Classical Hatha, Ashtanga & Vinyasa Flow Principles",
    "Functional Anatomy, Biomechanics & Injury Awareness",
    "Patanjali's Yoga Sutras & Bhagavad Gita Philosophy",
    "Advanced Asana Alignment, Progressions & Modifications",
    "Pranayama, Breath Retention (Kumbhaka) & Subtle Body",
    "Meditation, Dharana, Dhyana & Yoga Nidra Training",
    "Mudras, Bandhas, Nadis & Chakra Energy System",
    "Class Architecture, Peak Posture Sequencing & Pacing",
    "PranaFlow Student Dashboard Portal Access",
    "Same-Day Class Video Recordings for Catch-Up",
    "Small Interactive Batches (Max 25 Students)",
    "PranaFlow Academy 500-Hour Yoga Teacher Certificate"
  ];

  const bonuses = [
    "500-Hour Master Manual",
    "18-Module Study Guides",
    "Asana Alignment Chart",
    "Sequencing Template Pack",
    "Pranayama & Sutra Guide",
    "Ayurvedic Ahara Handbook",
    "PranaFlow Certificate"
  ];

  const myths = [
    { m: '"You must be able to do extreme advanced poses to join 500-hour training."', f: 'A 500-hour master training is about understanding yoga deeply, intelligent alignment, anatomy, and teaching methodology — not performing circus tricks.' },
    { m: '"500-hour training is only for full-time professional teachers."', f: 'Many dedicated practitioners join to deepen their personal practice, study philosophy, understand anatomy, and experience inner transformation.' },
    { m: '"Online yoga teacher training is just watching pre-recorded videos."', f: 'PranaFlow Academy delivers live interactive classes with real-time mentor feedback, Q&A, and same-day recording access for catch-up.' },
    { m: '"You can master yoga philosophy by reading one book."', f: 'Yogic philosophy requires structured study over time, self-inquiry, and guided discussion to integrate into your mind and teaching voice.' },
    { m: '"All students in a class can do the same pose with the same cues."', f: 'Every body has unique bone structure and joint flexibility. A true 500-hour trained teacher knows how to adapt postures for all bodies.' },
    { m: '"Pranayama is just deep breathing."', f: 'Pranayama is a precise energetic science involving breath ratios, Bandhas, Kumbhaka, and nervous system regulation.' }
  ];

  const diffPoints = [
    { title: "500 Training Days Journey", desc: "A progressive, steady 500-day curriculum designed for long-term depth and mastery without burnout." },
    { title: "Live Daily Instruction", desc: "Real-time live classes with experienced educators from Rishikesh, India, offering direct guidance." },
    { title: "18 Comprehensive Modules", desc: "Covers philosophy, functional anatomy, advanced asana, pranayama, meditation, sequencing, and ethics." },
    { title: "PranaFlow Student Dashboard", desc: "All course recordings, schedule, study guides, and sequencing templates organized in your portal." },
    { title: "Same-Day Recording Access", desc: "Missed a live class due to work or travel? Watch recorded lectures uploaded to your dashboard on the same day." },
    { title: "Practical Teaching Practicum", desc: "Deliver live teaching demonstrations, receive peer and instructor feedback, and refine your teacher voice." },
    { title: "Small Interactive Batches", desc: "Limited to maximum 25 students per batch for direct guidance and personal mentorship." },
  ];

  return (
    <div className="pcos-page public-site">
      <PublicHeader />

      <main className="main">
        <div className="fy-header-spacer"></div>

        {/* 1. HERO SECTION */}
        <section id="hero" className="fy-section">
          <div className="fy-hero-container">
            <div className="fy-hero-wrapper">
              <div className="row align-items-center gy-4">
                <div className="col-lg-6">
                  <span className="fy-hero-pill">
                    <i className="bi bi-star-fill text-warning me-1"></i> Live Online Master-Level Course
                  </span>
                  <h1 className="fy-hero-title">500 Hour Yoga Teacher Training Online Rishikesh</h1>
                  <div className="fy-hero-subhead">Go Beyond Practising Yoga. Study It Deeply. Learn to Teach With Knowledge, Confidence &amp; Purpose.</div>

                  <p className="fy-hero-para">
                    500 Hours of Live, Interactive Yoga Teacher Training From Rishikesh, India.
                  </p>

                  <p className="fy-hero-para">
                    Step into the highest level of professional yoga education with PranaFlow Academy's Online 500 Hour Yoga Teacher Training — a comprehensive, expert-led master-level program designed for dedicated yoga practitioners and certified teachers ready to achieve complete depth, authority, and confidence in their practice and teaching — with all sessions recorded and managed through your PranaFlow Student Dashboard.
                  </p>

                  <div className="fy-btn-group">
                    <button type="button" className="fy-btn-primary" onClick={() => setIsEnrollModalOpen(true)}>
                      Enroll Now <i className="bi bi-arrow-right-circle-fill"></i>
                    </button>
                    <button type="button" className="fy-btn-secondary" onClick={() => openModal(courseTitle, dbCourse)}>
                      Enquire Now
                    </button>
                    {demoInfo && demoInfo.available && (
                      <button 
                        type="button"
                        className="fy-btn-secondary" 
                        onClick={() => navigate(`/demo-class?course=${demoInfo.courseSlug || '500hr-yoga-ttc'}&course_id=${dbCourse ? dbCourse.id : ''}&demo_course_id=${demoInfo.demoCourseId}`, { state: { course: dbCourse } })}
                      >
                        {demoInfo.demoFee > 0 
                          ? `Demo Class — ₹${Number(demoInfo.demoFee)}` 
                          : 'Demo Class'}
                      </button>
                    )}
                  </div>

                  <div className="fy-trust-badges">
                    <span><i className="bi bi-clock-fill"></i> 500 Hours Training</span>
                    <span><i className="bi bi-calendar-event"></i> 500 Training Days</span>
                    <span><i className="bi bi-people-fill"></i> Max 25 Per Batch</span>
                    <span><i className="bi bi-award-fill"></i> Academy Certificate</span>
                  </div>
                </div>

                <div className="col-lg-6">
                  <div className="fy-hero-img-container">
                    <img src={heroCourseImage} alt="500 Hour Yoga Teacher Training Online" loading="eager" />
                  </div>
                </div>
              </div>

              {/* Feature Callout Strip */}
              <div className="fy-strip-container">
                <div className="fy-strip-grid">
                  <div className="fy-strip-item">
                    <div className="fy-strip-icon sage"><i className="bi bi-clock-history"></i></div>
                    <div className="fy-strip-label">500 Hours<br />500 Days</div>
                  </div>
                  <div className="fy-strip-item">
                    <div className="fy-strip-icon blush"><i className="bi bi-camera-video-fill"></i></div>
                    <div className="fy-strip-label">Live Online<br />Training</div>
                  </div>
                  <div className="fy-strip-item">
                    <div className="fy-strip-icon sage"><i className="bi bi-journal-bookmark"></i></div>
                    <div className="fy-strip-label">18 Detailed<br />Modules</div>
                  </div>
                  <div className="fy-strip-item">
                    <div className="fy-strip-icon blush"><i className="bi bi-person-check-fill"></i></div>
                    <div className="fy-strip-label">Teaching<br />Practicum</div>
                  </div>
                  <div className="fy-strip-item">
                    <div className="fy-strip-icon sage"><i className="bi bi-speedometer2"></i></div>
                    <div className="fy-strip-label">Student<br />Dashboard</div>
                  </div>
                  <div className="fy-strip-item">
                    <div className="fy-strip-icon blush"><i className="bi bi-credit-card-2-back"></i></div>
                    <div className="fy-strip-label">Installment<br />Available</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 2. OVERVIEW — "Where Digital Accessibility Meets Authentic Yoga Education" */}
        <section className="fy-section">
          <div className="fy-container">
            <div className="fy-plan-box">
              <h2 className="fy-serif-heading mb-4 text-center text-lg-start">Where Digital Accessibility Meets Authentic Yoga Education</h2>

              <div className="row gy-4 align-items-center">
                <div className="col-lg-6">
                  <p className="fy-hero-para">
                    PranaFlow Academy was built on a simple belief: quality yoga education should be accessible to every serious student, anywhere in the world.
                  </p>
                  <p className="fy-hero-para">
                    We connect advanced yoga teachers and dedicated practitioners with experienced, certified yoga educators from Rishikesh and across India. Through carefully structured live online classes (~1 hour per day over 500 days), we deliver master-level yoga training directly to your home.
                  </p>

                  <div className="fy-disclaimer-box">
                    <i className="bi bi-info-circle-fill"></i>
                    <div>
                      "We don't just stream yoga classes. We build real yoga teachers." — PranaFlow Academy
                    </div>
                  </div>
                </div>

                <div className="col-lg-6">
                  <div className="fy-benefit-pill"><i className="bi bi-check-circle-fill"></i><span>Advanced Hatha, Ashtanga &amp; Vinyasa Flow posture mechanics</span></div>
                  <div className="fy-benefit-pill"><i className="bi bi-check-circle-fill"></i><span>Functional anatomy, joint biomechanics &amp; injury prevention</span></div>
                  <div className="fy-benefit-pill"><i className="bi bi-check-circle-fill"></i><span>Yoga Sutras, Bhagavad Gita, 8 Limbs &amp; yogic philosophy</span></div>
                  <div className="fy-benefit-pill"><i className="bi bi-check-circle-fill"></i><span>Pranayama, Kumbhaka, Bandhas, Nadis &amp; energy body science</span></div>
                  <div className="fy-benefit-pill"><i className="bi bi-check-circle-fill"></i><span>Class architecture, peak pose sequencing &amp; adaptative teaching</span></div>
                  <div className="fy-benefit-pill"><i className="bi bi-check-circle-fill"></i><span>PranaFlow Student Dashboard with daily video access</span></div>
                  <div className="fy-benefit-pill mb-0"><i className="bi bi-check-circle-fill"></i><span>Live teaching practicum, mentor evaluation &amp; 500-hour cert</span></div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 3. HIGHLIGHTS & BONUS — "What You Will Cover" */}
        <section id="highlights" className="fy-section fy-section-alt">
          <div className="fy-container">
            <div className="fy-section-title-wrap">
              <h2 className="fy-serif-heading">What You Will Cover</h2>
              <div className="fy-title-underline"></div>
            </div>

            <div className="row gy-4 align-items-stretch">
              <div className="col-lg-8">
                <div className="fy-highlights-grid">
                  <div>
                    <div className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>Foundations, History &amp; Evolution of Yoga</span></div>
                    <div className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>Yoga Sutras &amp; Eight Limbs Philosophy</span></div>
                    <div className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>Bhagavad Gita &amp; Yogic Lifestyle Science</span></div>
                    <div className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>Hatha, Ashtanga &amp; Vinyasa Flow Asana</span></div>
                    <div className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>Advanced Alignment, Inversions &amp; Backbends</span></div>
                  </div>

                  <div>
                    <div className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>Functional Anatomy &amp; Biomechanics</span></div>
                    <div className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>Physiology &amp; Nervous System Regulation</span></div>
                    <div className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>Pranayama &amp; Breath Retention (Kumbhaka)</span></div>
                    <div className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>Meditation, Dharana, Dhyana &amp; Yoga Nidra</span></div>
                    <div className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>Mudras, Bandhas &amp; Subtle Energy Body</span></div>
                  </div>

                  <div>
                    <div className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>Mantra Chanting &amp; Vedic Sound Science</span></div>
                    <div className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>Class Architecture &amp; Peak Pose Sequencing</span></div>
                    <div className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>Adaptation, Props &amp; Inclusive Modifications</span></div>
                    <div className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>Ayurveda Ahara &amp; Dinacharya Lifestyle</span></div>
                    <div className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>500-Hour Yoga Teacher Certificate</span></div>
                  </div>
                </div>
              </div>

              <div className="col-lg-4">
                <div className="fy-bonus-card">
                  <div className="fy-bonus-icon"><i className="bi bi-gift-fill"></i></div>
                  <div className="fy-bonus-title">INCLUDED RESOURCES</div>
                  <div className="fy-bonus-sub">Manuals, Guides &amp; Templates</div>
                  <ul className="fy-bonus-list">
                    {bonuses.map((b, i) => (
                      <li key={i}><i className="bi bi-check2-circle"></i>{b}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 4. CURRICULUM OVERVIEW — 8 Pillars & 18 Modules Accordion */}
        <section id="curriculum" className="fy-section">
          <div className="fy-container">
            <div className="fy-section-title-wrap">
              <h2 className="fy-serif-heading">The 8 Core Pillars of Your 500-Hour Training</h2>
              <div className="fy-title-underline"></div>
            </div>

            <div className="fy-curriculum-grid mb-4">
              {pillars.map((p, i) => (
                <div key={i} className="fy-week-card">
                  <div className="fy-week-icon"><i className="bi bi-flower2"></i></div>
                  <span className={`fy-week-badge ${i % 2 === 0 ? 'sage' : 'blush'}`}>{p.num}</span>
                  <h3 className="fy-week-title">{p.title}</h3>
                  <ul className="fy-week-list">
                    {p.tags.map((t, j) => <li key={j}>{t}</li>)}
                  </ul>
                  <p style={{ fontSize: '0.85rem', color: 'var(--fy-text-muted)', marginTop: '10px', lineHeight: 1.5 }}>{p.desc}</p>
                </div>
              ))}
            </div>

            {/* 18-Module Syllabus Accordion */}
            <div className="fy-section-title-wrap mt-5 mb-4">
              <h3 className="fy-serif-heading" style={{ fontSize: '1.6rem' }}>Complete 18-Module Detailed Syllabus</h3>
              <p className="fy-hero-para" style={{ maxWidth: '650px', margin: '8px auto 0' }}>
                Click on any module to view the detailed topics and descriptions in our 500-hour master curriculum.
              </p>
            </div>

            <div className="fy-syllabus-container">
              {modules.map((m, idx) => (
                <AccordionItem
                  key={idx}
                  num={m.num}
                  title={m.title}
                  isActive={activeModule === idx}
                  onToggle={() => setActiveModule(activeModule === idx ? null : idx)}
                >
                  <p style={{ fontSize: '0.9rem', color: 'var(--fy-text-main)', lineHeight: 1.65, margin: 0 }}>
                    {m.desc}
                  </p>
                </AccordionItem>
              ))}
            </div>
          </div>
        </section>

        {/* 5. TEACHING PRACTICUM JOURNEY */}
        <section id="practicum" className="fy-section fy-section-alt">
          <div className="fy-container">
            <div className="fy-section-title-wrap">
              <h2 className="fy-serif-heading">Teaching Practicum — From Practitioner to Master Teacher</h2>
              <div className="fy-title-underline"></div>
              <p className="fy-hero-para mt-3" style={{ maxWidth: '650px', margin: '12px auto 0' }}>
                Practical teaching experience is built into the training so you graduate with authentic confidence.
              </p>
            </div>

            <div className="row gy-4 text-center">
              <div className="col-md-4 col-lg-2">
                <div style={{ background: 'var(--fy-cream-card)', border: '1px solid var(--fy-sage-border)', borderRadius: 'var(--fy-radius-md)', padding: '20px 12px' }}>
                  <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: 'var(--fy-sage-light)', color: 'var(--fy-sage-dark)', fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 10px' }}>1</div>
                  <h4 style={{ fontSize: '0.95rem', fontWeight: 700, margin: '0 0 4px' }}>Plan</h4>
                  <p style={{ fontSize: '0.8rem', color: 'var(--fy-text-muted)', margin: 0 }}>Design intelligent class sequences</p>
                </div>
              </div>

              <div className="col-md-4 col-lg-2">
                <div style={{ background: 'var(--fy-cream-card)', border: '1px solid var(--fy-sage-border)', borderRadius: 'var(--fy-radius-md)', padding: '20px 12px' }}>
                  <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: 'var(--fy-blush-light)', color: 'var(--fy-blush-dark)', fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 10px' }}>2</div>
                  <h4 style={{ fontSize: '0.95rem', fontWeight: 700, margin: '0 0 4px' }}>Teach</h4>
                  <p style={{ fontSize: '0.8rem', color: 'var(--fy-text-muted)', margin: 0 }}>Lead live practice teaching sessions</p>
                </div>
              </div>

              <div className="col-md-4 col-lg-2">
                <div style={{ background: 'var(--fy-cream-card)', border: '1px solid var(--fy-sage-border)', borderRadius: 'var(--fy-radius-md)', padding: '20px 12px' }}>
                  <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: 'var(--fy-sage-light)', color: 'var(--fy-sage-dark)', fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 10px' }}>3</div>
                  <h4 style={{ fontSize: '0.95rem', fontWeight: 700, margin: '0 0 4px' }}>Observe</h4>
                  <p style={{ fontSize: '0.8rem', color: 'var(--fy-text-muted)', margin: 0 }}>Analyze student alignment &amp; movement</p>
                </div>
              </div>

              <div className="col-md-4 col-lg-2">
                <div style={{ background: 'var(--fy-cream-card)', border: '1px solid var(--fy-sage-border)', borderRadius: 'var(--fy-radius-md)', padding: '20px 12px' }}>
                  <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: 'var(--fy-blush-light)', color: 'var(--fy-blush-dark)', fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 10px' }}>4</div>
                  <h4 style={{ fontSize: '0.95rem', fontWeight: 700, margin: '0 0 4px' }}>Receive Feedback</h4>
                  <p style={{ fontSize: '0.8rem', color: 'var(--fy-text-muted)', margin: 0 }}>Get direct mentor evaluation</p>
                </div>
              </div>

              <div className="col-md-4 col-lg-2">
                <div style={{ background: 'var(--fy-cream-card)', border: '1px solid var(--fy-sage-border)', borderRadius: 'var(--fy-radius-md)', padding: '20px 12px' }}>
                  <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: 'var(--fy-sage-light)', color: 'var(--fy-sage-dark)', fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 10px' }}>5</div>
                  <h4 style={{ fontSize: '0.95rem', fontWeight: 700, margin: '0 0 4px' }}>Reflect</h4>
                  <p style={{ fontSize: '0.8rem', color: 'var(--fy-text-muted)', margin: 0 }}>Refine voice &amp; cueing clarity</p>
                </div>
              </div>

              <div className="col-md-4 col-lg-2">
                <div style={{ background: 'var(--fy-cream-card)', border: '1px solid var(--fy-sage-border)', borderRadius: 'var(--fy-radius-md)', padding: '20px 12px' }}>
                  <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: '#DCF5E6', color: '#1A5C33', fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 10px' }}>6</div>
                  <h4 style={{ fontSize: '0.95rem', fontWeight: 700, margin: '0 0 4px' }}>Improve</h4>
                  <p style={{ fontSize: '0.8rem', color: 'var(--fy-text-muted)', margin: 0 }}>Master professional confidence</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 6. COURSE DETAILS / WHO IT IS FOR & PRICING CARD */}
        <section id="course-details" className="fy-section">
          <div className="fy-container">
            <div className="row gy-5 align-items-stretch">
              {/* Left image */}
              <div className="col-lg-4" style={{ textAlign: 'center' }}>
                <div className="fy-business-image-wrap">
                  <img src={courseBgImage} alt="500 Hour Yoga Teacher Training Online" className="img-fluid w-100" />
                </div>
              </div>

              {/* Center — Who it is for */}
              <div className="col-lg-4">
                <h2 className="fy-serif-heading mb-3">Who Is This Course For?</h2>
                <div className="text-muted fw-bold mb-3" style={{ fontSize: '0.95rem' }}>Designed for dedicated learners seeking depth.</div>

                <p className="fy-hero-para">
                  Dedicated practitioners ready to go deeper, aspiring yoga teachers seeking complete training, existing teachers advancing their credentials, wellness professionals, working professionals, and international students.
                </p>

                <div className="d-flex flex-column gap-2 mb-3">
                  <div className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>Yoga Practitioners Ready to Go Deeper</span></div>
                  <div className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>Aspiring Yoga Teachers Seeking Mastery</span></div>
                  <div className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>Existing Teachers Advancing Credentials</span></div>
                  <div className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>People Seeking Personal Transformation</span></div>
                  <div className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>Wellness Coaches &amp; Movement Professionals</span></div>
                  <div className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>International Students Seeking Authentic Study</span></div>
                </div>

                <div className="small text-muted fst-italic">
                  *500 training days delivered live online at approximately 1 hour per day with max 25 students per batch from Rishikesh, India.
                </div>
              </div>

              {/* Right — Pricing card V2 */}
              <div className="col-lg-4">
                {(() => {
                  const coupon = getBannerCouponFor500Hour();
                  const feeInr = dbCourse ? parseFloat(dbCourse.fee_inr) : 0;
                  const displayPrice = dbCourse ? formatPrice(dbCourse.fee_inr, dbCourse) : formatPrice(70000);
                  let formattedOriginalPrice = displayPrice;
                  let formattedDiscountedPrice = '';
                  let percentageVal = 0;

                  if (coupon && feeInr > 0) {
                    if (coupon.discount_type === 'percentage') {
                      percentageVal = Math.round(parseFloat(coupon.discount_value));
                      const discountedInr = feeInr * (1 - percentageVal / 100);
                      formattedDiscountedPrice = formatPrice(discountedInr, dbCourse);
                      formattedOriginalPrice = formatPrice(feeInr, dbCourse);
                    } else if (coupon.discount_type === 'flat' || coupon.discount_type === 'fixed') {
                      const fixedVal = parseFloat(coupon.discount_value);
                      const discountedInr = Math.max(0, feeInr - fixedVal);
                      percentageVal = Math.round((fixedVal / feeInr) * 100);
                      formattedDiscountedPrice = formatPrice(discountedInr, dbCourse);
                      formattedOriginalPrice = formatPrice(feeInr, dbCourse);
                    }
                  }

                  return (
                    <div className="fy-program-details-card-v2" id="pricing">
                      <h3 className="pcos-pd-heading">PROGRAM DETAILS</h3>
                      <div className="pcos-pd-heading-divider"></div>

                      <div className="pcos-pd-rows-wrap">
                        <div className="pcos-pd-row">
                          <div className="pcos-pd-label">
                            <i className="bi bi-calendar-event"></i>
                            <span>Duration</span>
                          </div>
                          <div className="pcos-pd-val">500 Hours / 500 Days</div>
                        </div>

                        <div className="pcos-pd-row">
                          <div className="pcos-pd-label">
                            <i className="bi bi-laptop"></i>
                            <span>Mode</span>
                          </div>
                          <div className="pcos-pd-val">Live Online Training</div>
                        </div>

                        <div className="pcos-pd-row">
                          <div className="pcos-pd-label">
                            <i className="bi bi-clock"></i>
                            <span>Timing</span>
                          </div>
                          <div className="pcos-pd-val">~1 Hour / Day</div>
                        </div>

                        <div className="pcos-pd-row">
                          <div className="pcos-pd-label">
                            <i className="bi bi-person-badge"></i>
                            <span>Batch Size</span>
                          </div>
                          <div className="pcos-pd-val">Max 25 Students</div>
                        </div>

                        <div className="pcos-pd-row">
                          <div className="pcos-pd-label">
                            <i className="bi bi-geo-alt"></i>
                            <span>Location</span>
                          </div>
                          <div className="pcos-pd-val">Rishikesh, India</div>
                        </div>

                        <div className="pcos-pd-row">
                          <div className="pcos-pd-label">
                            <i className="bi bi-journal-check"></i>
                            <span>Curriculum</span>
                          </div>
                          <div className="pcos-pd-val">18 Modules · Master Level</div>
                        </div>

                        <div className="pcos-pd-row">
                          <div className="pcos-pd-label">
                            <i className="bi bi-speedometer2"></i>
                            <span>Portal</span>
                          </div>
                          <div className="pcos-pd-val">PranaFlow Dashboard</div>
                        </div>

                        <div className="pcos-pd-row">
                          <div className="pcos-pd-label">
                            <i className="bi bi-award-fill"></i>
                            <span>Certificate</span>
                          </div>
                          <div className="pcos-pd-val">Yoga Alliance Cert</div>
                        </div>
                      </div>

                      <div className="pf-course-promo-badge pf-course-promo-badge-pd">
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                          <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"></path>
                          <line x1="7" y1="7" x2="7.01" y2="7"></line>
                        </svg>
                        <span>Every batch: First 5 students get an extra discount on fee!</span>
                      </div>

                      {coupon ? (
                        <div className="pcos-pd-offer-box">
                          <div className="pcos-pd-offer-pill">
                            <i className="bi bi-star"></i> LIMITED TIME OFFER
                          </div>

                          <div className="pcos-pd-offer-prices-row">
                            <div className="pcos-pd-orig-price-wrap">
                              <span className="pcos-pd-metric-label">ORIGINAL FEE</span>
                              <span className="pcos-pd-orig-price">{formattedOriginalPrice}</span>
                            </div>

                            <div className="pcos-pd-disc-price-wrap">
                              <span className="pcos-pd-metric-label">DISCOUNTED FEE</span>
                              <span className="pcos-pd-disc-price">{formattedDiscountedPrice}</span>
                            </div>

                            <div className="pcos-pd-badge-wrap">
                              <div className="pcos-pd-badge-rect">
                                <span className="pcos-pd-badge-num">{percentageVal}%</span>
                                <span className="pcos-pd-badge-text">OFF</span>
                              </div>
                            </div>
                          </div>

                          <div className="pcos-pd-timer-strip">
                            <i className="bi bi-stopwatch"></i>
                            <span>
                              {formatCountdown(coupon.banner_expiry || coupon.expiry_date).includes('day') ? 'Offer ends in' : 'Offer valid today'} · {formatCountdown(coupon.banner_expiry || coupon.expiry_date)}
                            </span>
                          </div>
                        </div>
                      ) : (
                        <div className="pcos-pd-normal-box">
                          <span className="pcos-pd-normal-label">TUITION FEE</span>
                          <span className="pcos-pd-normal-price">{loadingBatches ? 'Loading...' : displayPrice}</span>
                        </div>
                      )}

                      <button
                        type="button"
                        className="pcos-pd-enroll-btn"
                        onClick={() => setIsEnrollModalOpen(true)}
                      >
                        <span>Enroll Now &amp; Reserve Your Seat</span>
                        <div className="pcos-pd-btn-arrow-circle">
                          <i className="bi bi-arrow-right"></i>
                        </div>
                      </button>

                      <button
                        type="button"
                        className="fy-btn-secondary w-100 mt-2"
                        onClick={() => openModal(courseTitle, dbCourse)}
                        style={{
                          borderRadius: '30px',
                          padding: '12px 20px',
                          fontWeight: 700,
                          fontSize: '0.9rem',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '8px'
                        }}
                      >
                        Enquire Now <i className="bi bi-chat-dots"></i>
                      </button>

                      <div className="pcos-pd-next-batch-strip">
                        <i className="bi bi-leaf"></i>
                        <span>Next batch admissions open — inquire now</span>
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>
        </section>

        {/* NEW COMPARISON SECTION: 200 Hour vs 300 Hour vs 500 Hour Yoga Teacher Training */}
        <section id="pathways" className="fy-section fy-section-alt">
          <div className="fy-container">
            <div className="fy-section-title-wrap">
              <span className="fy-eyebrow" style={{ display: 'inline-block', color: 'var(--fy-blush-dark)', fontWeight: 700, fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px' }}>
                CHOOSE YOUR TRAINING PATH
              </span>
              <h2 className="fy-serif-heading">200 Hour vs 300 Hour vs 500 Hour Yoga Teacher Training</h2>
              <div className="fy-title-underline"></div>
              <p className="fy-hero-para mt-3" style={{ maxWidth: '800px', margin: '14px auto 0' }}>
                Choosing the right yoga teacher training depends on where you are in your practice, how deeply you want to study, and the level of teaching knowledge you want to develop.
              </p>
              <p className="fy-hero-para mt-2" style={{ maxWidth: '800px', margin: '8px auto 0', fontSize: '0.95rem', color: 'var(--fy-text-muted)' }}>
                At PranaFlow Academy, the 200-hour, 300-hour, and 500-hour pathways serve different stages of a yoga teacher's learning journey. Understanding the difference can help you choose a program that matches your present experience and long-term teaching goals.
              </p>
            </div>

            {/* 3 Comparison Cards Grid */}
            <div className="row gy-4 align-items-stretch mb-5">
              {/* Card 1: 200 Hour */}
              <div className="col-lg-4 col-md-6">
                <div style={{
                  background: 'var(--fy-cream-card)',
                  border: '1px solid var(--fy-sage-border)',
                  borderRadius: 'var(--fy-radius-lg)',
                  padding: '32px 24px',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  boxShadow: 'var(--fy-shadow-card)',
                  transition: 'var(--fy-transition)'
                }}>
                  <div>
                    <span style={{
                      display: 'inline-block',
                      background: 'var(--fy-sage-light)',
                      color: 'var(--fy-sage-dark)',
                      fontSize: '0.75rem',
                      fontWeight: 800,
                      padding: '4px 12px',
                      borderRadius: 'var(--fy-radius-pill)',
                      letterSpacing: '0.5px',
                      marginBottom: '14px'
                    }}>
                      FOUNDATIONAL PATHWAY
                    </span>
                    <h3 style={{ fontSize: '1.35rem', fontWeight: 800, color: 'var(--fy-text-main)', marginBottom: '12px', fontFamily: 'var(--heading-font-family)' }}>
                      200 Hour Yoga Teacher Training
                    </h3>
                    <p style={{ fontSize: '0.9rem', color: 'var(--fy-text-main)', lineHeight: 1.65, marginBottom: '20px' }}>
                      The <strong>200 Hour Yoga Teacher Training</strong> is designed to build a strong foundation in yoga practice and teaching. It introduces students to essential areas such as asana practice, pranayama, yoga philosophy, anatomy, teaching methodology, alignment, sequencing, and practical teaching skills. This pathway is well suited to aspiring yoga teachers and dedicated practitioners who want structured foundational training before progressing into deeper levels of yoga education.
                    </p>
                  </div>
                  <div>
                    <Link
                      to="/courses/200-hour-yoga-teacher-training-rishikesh"
                      className="fy-btn-secondary w-100"
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                        textDecoration: 'none',
                        textAlign: 'center',
                        fontSize: '0.88rem',
                        fontWeight: 700,
                        padding: '12px 18px',
                        borderRadius: 'var(--fy-radius-pill)'
                      }}
                    >
                      Explore the 200 Hour Yoga Teacher Training <i className="bi bi-arrow-right"></i>
                    </Link>
                  </div>
                </div>
              </div>

              {/* Card 2: 300 Hour */}
              <div className="col-lg-4 col-md-6">
                <div style={{
                  background: 'var(--fy-cream-card)',
                  border: '1px solid var(--fy-blush-border)',
                  borderRadius: 'var(--fy-radius-lg)',
                  padding: '32px 24px',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justify: 'space-between',
                  boxShadow: 'var(--fy-shadow-card)',
                  transition: 'var(--fy-transition)'
                }}>
                  <div>
                    <span style={{
                      display: 'inline-block',
                      background: 'var(--fy-blush-light)',
                      color: 'var(--fy-blush-dark)',
                      fontSize: '0.75rem',
                      fontWeight: 800,
                      padding: '4px 12px',
                      borderRadius: 'var(--fy-radius-pill)',
                      letterSpacing: '0.5px',
                      marginBottom: '14px'
                    }}>
                      ADVANCED PATHWAY
                    </span>
                    <h3 style={{ fontSize: '1.35rem', fontWeight: 800, color: 'var(--fy-text-main)', marginBottom: '12px', fontFamily: 'var(--heading-font-family)' }}>
                      300 Hour Yoga Teacher Training
                    </h3>
                    <p style={{ fontSize: '0.9rem', color: 'var(--fy-text-main)', lineHeight: 1.65, marginBottom: '20px' }}>
                      The <strong>300 Hour Yoga Teacher Training</strong> is intended for students who want to move beyond foundational study and explore yoga with greater technical, philosophical, and teaching depth. The training provides an opportunity to strengthen teaching methodology, refine personal practice, expand knowledge across advanced yoga subjects, and develop greater confidence as a yoga educator. It is particularly relevant for practitioners and teachers who already have foundational yoga training and are ready for more advanced study.
                    </p>
                  </div>
                  <div>
                    <Link
                      to="/courses/300hr-yoga-ttc"
                      className="fy-btn-secondary w-100"
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                        textDecoration: 'none',
                        textAlign: 'center',
                        fontSize: '0.88rem',
                        fontWeight: 700,
                        padding: '12px 18px',
                        borderRadius: 'var(--fy-radius-pill)'
                      }}
                    >
                      Explore the 300 Hour Yoga Teacher Training <i className="bi bi-arrow-right"></i>
                    </Link>
                  </div>
                </div>
              </div>

              {/* Card 3: 500 Hour (Highlighted) */}
              <div className="col-lg-4 col-md-12">
                <div style={{
                  background: 'var(--fy-white)',
                  border: '2px solid var(--fy-sage-primary)',
                  borderRadius: 'var(--fy-radius-lg)',
                  padding: '32px 24px',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justify: 'space-between',
                  boxShadow: 'var(--fy-shadow-raised)',
                  position: 'relative'
                }}>
                  <div style={{
                    position: 'absolute',
                    top: '-12px',
                    right: '24px',
                    background: 'var(--fy-sage-primary)',
                    color: '#ffffff',
                    fontSize: '0.7rem',
                    fontWeight: 800,
                    padding: '3px 12px',
                    borderRadius: 'var(--fy-radius-pill)',
                    letterSpacing: '0.5px'
                  }}>
                    THIS COURSE
                  </div>
                  <div>
                    <span style={{
                      display: 'inline-block',
                      background: '#DCF5E6',
                      color: '#1A5C33',
                      fontSize: '0.75rem',
                      fontWeight: 800,
                      padding: '4px 12px',
                      borderRadius: 'var(--fy-radius-pill)',
                      letterSpacing: '0.5px',
                      marginBottom: '14px'
                    }}>
                      MASTER-LEVEL LEARNING PATHWAY
                    </span>
                    <h3 style={{ fontSize: '1.35rem', fontWeight: 800, color: 'var(--fy-sage-dark)', marginBottom: '12px', fontFamily: 'var(--heading-font-family)' }}>
                      500 Hour Yoga Teacher Training
                    </h3>
                    <p style={{ fontSize: '0.9rem', color: 'var(--fy-text-main)', lineHeight: 1.65, marginBottom: '20px' }}>
                      The <strong>500 Hour Yoga Teacher Training</strong> is for students who want a much more comprehensive and long-form study of yoga rather than limiting their learning to a shorter training experience. PranaFlow Academy's 500-hour pathway provides <strong>500 hours of live online learning</strong> across an extensive curriculum covering yoga practice, philosophy, anatomy, pranayama, teaching methodology, practicum, and other core areas already presented throughout this program page. With approximately one hour of guided live learning per day across the program, students have more time to study, practise, review concepts, develop teaching ability, and integrate yoga into their professional and personal journey. It is designed for dedicated practitioners, existing yoga teachers, and wellness professionals who want broader knowledge and greater teaching depth.
                    </p>
                  </div>
                  <div>
                    <button
                      type="button"
                      className="fy-btn-primary w-100"
                      onClick={() => scrollTo('enrollment')}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                        fontSize: '0.88rem',
                        fontWeight: 700,
                        padding: '12px 18px',
                        borderRadius: 'var(--fy-radius-pill)'
                      }}
                    >
                      Choose the 500 Hour Master Training <i className="bi bi-arrow-right-circle-fill"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Why Choose the 500-Hour Pathway? Benefit Grid */}
            <div className="fy-section-title-wrap mt-5 mb-4">
              <h3 className="fy-serif-heading" style={{ fontSize: '1.6rem' }}>Why Choose the 500 Hour Yoga Teacher Training?</h3>
              <div className="fy-title-underline"></div>
            </div>

            <div className="row gy-4 mb-5">
              <div className="col-md-6 col-lg-3">
                <div style={{ background: 'var(--fy-cream-card)', border: '1px solid var(--fy-sage-border)', borderRadius: 'var(--fy-radius-md)', padding: '24px 20px', height: '100%' }}>
                  <div style={{ width: '44px', height: '44px', borderRadius: '50%', background: 'var(--fy-sage-light)', color: 'var(--fy-sage-dark)', fontWeight: 800, fontSize: '0.9rem', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '14px' }}>01</div>
                  <h4 style={{ fontSize: '1.05rem', fontWeight: 800, color: 'var(--fy-sage-dark)', marginBottom: '8px' }}>More Time to Understand, Not Just Memorise</h4>
                  <p style={{ fontSize: '0.85rem', color: 'var(--fy-text-muted)', lineHeight: 1.6, margin: 0 }}>
                    A longer learning pathway gives you more time to understand concepts, practise techniques, ask questions, revise lessons, and gradually develop confidence instead of trying to absorb advanced yoga education within a short period.
                  </p>
                </div>
              </div>

              <div className="col-md-6 col-lg-3">
                <div style={{ background: 'var(--fy-cream-card)', border: '1px solid var(--fy-sage-border)', borderRadius: 'var(--fy-radius-md)', padding: '24px 20px', height: '100%' }}>
                  <div style={{ width: '44px', height: '44px', borderRadius: '50%', background: 'var(--fy-blush-light)', color: 'var(--fy-blush-dark)', fontWeight: 800, fontSize: '0.9rem', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '14px' }}>02</div>
                  <h4 style={{ fontSize: '1.05rem', fontWeight: 800, color: 'var(--fy-sage-dark)', marginBottom: '8px' }}>Deeper Study Across Multiple Yoga Disciplines</h4>
                  <p style={{ fontSize: '0.85rem', color: 'var(--fy-text-muted)', lineHeight: 1.6, margin: 0 }}>
                    The 500-hour curriculum allows students to explore a wider range of subjects and develop stronger connections between physical practice, philosophy, anatomy, pranayama, teaching methodology, and practical teaching experience.
                  </p>
                </div>
              </div>

              <div className="col-md-6 col-lg-3">
                <div style={{ background: 'var(--fy-cream-card)', border: '1px solid var(--fy-sage-border)', borderRadius: 'var(--fy-radius-md)', padding: '24px 20px', height: '100%' }}>
                  <div style={{ width: '44px', height: '44px', borderRadius: '50%', background: 'var(--fy-sage-light)', color: 'var(--fy-sage-dark)', fontWeight: 800, fontSize: '0.9rem', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '14px' }}>03</div>
                  <h4 style={{ fontSize: '1.05rem', fontWeight: 800, color: 'var(--fy-sage-dark)', marginBottom: '8px' }}>Build Greater Teaching Confidence</h4>
                  <p style={{ fontSize: '0.85rem', color: 'var(--fy-text-muted)', lineHeight: 1.6, margin: 0 }}>
                    Extended training provides more opportunities to observe, practise, refine, and apply teaching skills so that knowledge can gradually develop into practical teaching ability.
                  </p>
                </div>
              </div>

              <div className="col-md-6 col-lg-3">
                <div style={{ background: 'var(--fy-cream-card)', border: '1px solid var(--fy-sage-border)', borderRadius: 'var(--fy-radius-md)', padding: '24px 20px', height: '100%' }}>
                  <div style={{ width: '44px', height: '44px', borderRadius: '50%', background: 'var(--fy-blush-light)', color: 'var(--fy-blush-dark)', fontWeight: 800, fontSize: '0.9rem', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '14px' }}>04</div>
                  <h4 style={{ fontSize: '1.05rem', fontWeight: 800, color: 'var(--fy-sage-dark)', marginBottom: '8px' }}>Suitable for Serious Long-Term Yoga Study</h4>
                  <p style={{ fontSize: '0.85rem', color: 'var(--fy-text-muted)', lineHeight: 1.6, margin: 0 }}>
                    This pathway is particularly valuable for students who see yoga as more than a short certification course and want structured, sustained education that supports their long-term development as practitioners and teachers.
                  </p>
                </div>
              </div>
            </div>

            {/* Decision / Sales Block */}
            <div style={{
              background: 'linear-gradient(135deg, var(--fy-sage-dark) 0%, var(--fy-sage-primary) 100%)',
              borderRadius: 'var(--fy-radius-lg)',
              padding: '36px 32px',
              color: '#ffffff',
              boxShadow: 'var(--fy-shadow-raised)'
            }}>
              <div className="row align-items-center gy-4">
                <div className="col-lg-8">
                  <h3 style={{ fontSize: '1.4rem', fontWeight: 800, color: '#ffffff', marginBottom: '16px', fontFamily: 'var(--heading-font-family)' }}>
                    Which Yoga Teacher Training Should You Choose?
                  </h3>
                  <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 16px', display: 'flex', flexDirection: 'column', gap: '10px', fontSize: '0.92rem', color: '#E8F5EB', lineHeight: 1.6 }}>
                    <li style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                      <i className="bi bi-check-circle-fill" style={{ color: '#A3D4B0', marginTop: '3px' }}></i>
                      <span>Choose the <strong>200-hour pathway</strong> if you are looking for structured foundational yoga teacher training.</span>
                    </li>
                    <li style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                      <i className="bi bi-check-circle-fill" style={{ color: '#A3D4B0', marginTop: '3px' }}></i>
                      <span>Choose the <strong>300-hour pathway</strong> if you already have foundational knowledge and want to advance your practice and teaching skills.</span>
                    </li>
                    <li style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                      <i className="bi bi-check-circle-fill" style={{ color: '#A3D4B0', marginTop: '3px' }}></i>
                      <span>Choose the <strong>500-hour pathway</strong> if you want the most comprehensive learning experience offered through this training pathway, with significantly more time to study, practise, integrate, and develop your knowledge across a broad yoga curriculum.</span>
                    </li>
                  </ul>
                </div>

                <div className="col-lg-4 text-center text-lg-end">
                  <button
                    type="button"
                    className="fy-btn-primary"
                    onClick={() => scrollTo('enrollment')}
                    style={{
                      background: '#ffffff',
                      color: 'var(--fy-sage-dark)',
                      border: 'none',
                      fontWeight: 800,
                      padding: '14px 24px',
                      borderRadius: 'var(--fy-radius-pill)',
                      fontSize: '0.95rem'
                    }}
                  >
                    Choose the 500 Hour Master Training <i className="bi bi-arrow-right-circle-fill" style={{ color: 'var(--fy-sage-primary)' }}></i>
                  </button>
                </div>
              </div>
            </div>

          </div>
        </section>

        {/* 7. YOU CAN PRACTISE YOGA FOR YEARS WITHOUT TRULY UNDERSTANDING IT */}
        <section className="fy-section fy-section-alt">
          <div className="fy-container">
            <div className="fy-section-title-wrap">
              <h2 className="fy-serif-heading">You Can Practise Yoga for Years Without Truly Understanding It</h2>
              <div className="fy-title-underline"></div>
              <p className="fy-hero-para mt-3" style={{ maxWidth: '700px', margin: '12px auto 0' }}>
                500 Hours gives you space to go deeper beyond physical postures into authentic yoga mastery.
              </p>
            </div>

            <div className="fy-pain-grid">
              {challengePoints.map((p, i) => (
                <div key={i} className="fy-pain-card">
                  <span className="fy-pain-x">✕</span>
                  <div>
                    <h4 className="fy-pain-title">{p.title}</h4>
                    <span className="fy-pain-desc">{p.desc}</span>
                  </div>
                </div>
              ))}
            </div>

            <div style={{ background: 'var(--fy-cream-card)', border: '2px dashed var(--fy-sage-border)', borderRadius: 'var(--fy-radius-lg)', padding: '24px 32px', textAlign: 'center', marginTop: '32px' }}>
              <h3 style={{ fontSize: '1.15rem', fontWeight: 800, color: 'var(--fy-sage-primary)', marginBottom: '8px' }}>
                "We don’t just stream yoga classes. We build real yoga teachers." — PranaFlow Academy
              </h3>
              <p style={{ color: 'var(--fy-text-muted)', margin: 0, fontSize: '0.95rem' }}>
                Our 500-hour master training provides the long-term depth, live interaction, and structured mentorship required for true transformation.
              </p>
            </div>
          </div>
        </section>

        {/* 8. PRANAFLOW STUDENT DASHBOARD */}
        <section className="fy-section fy-dashboard-section">
          <div className="fy-container">
            <div className="row align-items-center gy-5">
              <div className="col-lg-7">
                <span className="fy-dashboard-badge">PranaFlow LMS Student Portal</span>
                <h2 className="fy-serif-heading mb-3" style={{ color: '#ffffff', fontSize: '2rem' }}>Missed a Live Class? Your Learning Continues.</h2>
                <p className="fy-hero-para" style={{ color: '#C3DFC8' }}>
                  Work, family, or travel should never disrupt your momentum. Upon enrollment, you receive access to your personal <strong style={{ color: '#ffffff' }}>PranaFlow Student Dashboard</strong>.
                </p>

                <div className="row gy-2 mb-4" style={{ fontSize: '0.95rem', color: '#E8F5EB' }}>
                  <div className="col-sm-6"><div className="fy-check-bullet" style={{ color: '#E8F5EB' }}><i className="bi bi-check-circle-fill" style={{ color: '#A3D4B0' }}></i><span>Daily Live Class Links &amp; Schedule</span></div></div>
                  <div className="col-sm-6"><div className="fy-check-bullet" style={{ color: '#E8F5EB' }}><i className="bi bi-check-circle-fill" style={{ color: '#A3D4B0' }}></i><span>Same-Day Class Video Recordings</span></div></div>
                  <div className="col-sm-6"><div className="fy-check-bullet" style={{ color: '#E8F5EB' }}><i className="bi bi-check-circle-fill" style={{ color: '#A3D4B0' }}></i><span>Missed-Class Catch-Up Portal</span></div></div>
                  <div className="col-sm-6"><div className="fy-check-bullet" style={{ color: '#E8F5EB' }}><i className="bi bi-check-circle-fill" style={{ color: '#A3D4B0' }}></i><span>Revision Access During Course</span></div></div>
                  <div className="col-sm-6"><div className="fy-check-bullet" style={{ color: '#E8F5EB' }}><i className="bi bi-check-circle-fill" style={{ color: '#A3D4B0' }}></i><span>Downloadable Study Manuals &amp; Sutra Guides</span></div></div>
                  <div className="col-sm-6"><div className="fy-check-bullet" style={{ color: '#E8F5EB' }}><i className="bi bi-check-circle-fill" style={{ color: '#A3D4B0' }}></i><span>Practicum &amp; Assessment Submission Portal</span></div></div>
                </div>

                <div style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 'var(--fy-radius-md)', padding: '20px 24px' }}>
                  <h4 style={{ fontSize: '1.05rem', fontWeight: 800, color: '#fbbf24', margin: '0 0 6px' }}>
                    Learn Live. Catch Up When Needed. Master Every Concept.
                  </h4>
                  <p style={{ margin: 0, fontSize: '0.9rem', color: '#C3DFC8', lineHeight: 1.6 }}>
                    Every live lecture is recorded and uploaded to your Student Dashboard on the same day so you can stay fully aligned with your batch.
                  </p>
                </div>
              </div>

              <div className="col-lg-5">
                <div className="fy-dashboard-card">
                  <h3 style={{ fontSize: '1.2rem', fontWeight: 800, color: 'var(--fy-sage-primary)', marginBottom: '16px' }}>Your Student Dashboard Includes:</h3>
                  <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 24px', display: 'flex', flexDirection: 'column', gap: '10px', fontSize: '0.9rem', color: 'var(--fy-text-main)' }}>
                    <li className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>500-Hour Master Course Access</span></li>
                    <li className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>Daily Live Class Schedule &amp; Links</span></li>
                    <li className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>18 Structured Course Modules</span></li>
                    <li className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>Uploaded Lecture Recordings</span></li>
                    <li className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>Master Manuals &amp; Sequencing Charts</span></li>
                    <li className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>Practicum Case Study Submission</span></li>
                  </ul>
                  <button type="button" className="fy-btn-primary w-100" onClick={() => scrollTo('enrollment')}>
                    Get Access Upon Enrollment
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 9. MYTH-BUSTING */}
        <section className="fy-section">
          <div className="fy-container">
            <div className="fy-section-title-wrap">
              <h2 className="fy-serif-heading">500-Hour Master Training Myth-Busting</h2>
              <div className="fy-title-underline"></div>
              <p className="fy-hero-para mt-3" style={{ maxWidth: '600px', margin: '12px auto 0' }}>
                Replacing common misconceptions about advanced 500-hour yoga teacher training.
              </p>
            </div>

            <div className="row gy-3">
              {myths.map((item, idx) => (
                <div key={idx} className="col-md-6 col-lg-4">
                  <div className="fy-myth-card">
                    <span className="fy-myth-badge">MYTH vs FACT</span>
                    <h4 className="fy-myth-q">{item.m}</h4>
                    <p className="fy-myth-a"><strong>Fact:</strong> {item.f}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 10. WHY 500 HOURS? */}
        <section className="fy-section fy-section-alt">
          <div className="fy-container">
            <div className="fy-section-title-wrap">
              <h2 className="fy-serif-heading">Why 500 Hours?</h2>
              <div className="fy-title-underline"></div>
            </div>

            <div className="fy-diff-grid">
              {diffPoints.map((card, idx) => (
                <div key={idx} className="fy-diff-card">
                  <h4 className="fy-diff-title">{card.title}</h4>
                  <p className="fy-diff-desc">{card.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 11. 500-HOUR ROADMAP */}
        <section className="fy-section">
          <div className="fy-container">
            <div className="fy-section-title-wrap">
              <h2 className="fy-serif-heading">Your 500-Hour Master Learning Journey</h2>
              <div className="fy-title-underline"></div>
            </div>

            <div className="fy-roadmap-grid">
              <div className="fy-roadmap-card month1">
                <div>
                  <span className="fy-roadmap-badge month1">Phase 1</span>
                  <h3 style={{ fontSize: '1.1rem', fontWeight: 800, color: 'var(--fy-text-main)', margin: '0 0 10px' }}>Foundations, Asana &amp; Philosophy</h3>
                  <ul className="fy-week-list" style={{ marginBottom: '14px' }}>
                    <li>Modules 01–03 — Evolution, Sutras &amp; Gita</li>
                    <li>Modules 04–05 — Hatha &amp; Advanced Asana</li>
                    <li>Modules 06–07 — Anatomy &amp; Physiology</li>
                  </ul>
                </div>
                <div style={{ background: 'var(--fy-sage-light)', border: '1px solid var(--fy-sage-border)', padding: '10px', borderRadius: 'var(--fy-radius-sm)', fontSize: '0.82rem', color: 'var(--fy-sage-dark)', fontWeight: 600 }}>
                  Goal: Build deep physical &amp; philosophical foundations.
                </div>
              </div>

              <div className="fy-roadmap-card month2">
                <div>
                  <span className="fy-roadmap-badge month2">Phase 2</span>
                  <h3 style={{ fontSize: '1.1rem', fontWeight: 800, color: 'var(--fy-text-main)', margin: '0 0 10px' }}>Energy Body, Pranayama &amp; Sound</h3>
                  <ul className="fy-week-list" style={{ marginBottom: '14px' }}>
                    <li>Modules 08–09 — Pranayama &amp; Meditation</li>
                    <li>Modules 10–11 — Mudras, Bandhas &amp; Mantras</li>
                    <li>Modules 12–13 — Class Architecture &amp; Cueing</li>
                  </ul>
                </div>
                <div style={{ background: 'var(--fy-blush-light)', border: '1px solid var(--fy-blush-border)', padding: '10px', borderRadius: 'var(--fy-radius-sm)', fontSize: '0.82rem', color: 'var(--fy-blush-dark)', fontWeight: 600 }}>
                  Goal: Master energetic practices &amp; class design.
                </div>
              </div>

              <div className="fy-roadmap-card month3">
                <div>
                  <span className="fy-roadmap-badge month3">Phase 3</span>
                  <h3 style={{ fontSize: '1.1rem', fontWeight: 800, color: 'var(--fy-text-main)', margin: '0 0 10px' }}>Adaptation, Practicum &amp; Certification</h3>
                  <ul className="fy-week-list" style={{ marginBottom: '14px' }}>
                    <li>Modules 14–16 — Ayurveda, Ethics &amp; Adaptation</li>
                    <li>Module 17 — Live Teaching Practicum</li>
                    <li>Module 18 — Final Evaluation &amp; Certification</li>
                  </ul>
                </div>
                <div style={{ background: '#DCF5E6', border: '1px solid #A7D3B6', padding: '10px', borderRadius: 'var(--fy-radius-sm)', fontSize: '0.82rem', color: '#1A5C33', fontWeight: 600 }}>
                  Goal: Complete practicum &amp; earn 500-hour master cert.
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 12. ENROLLMENT & SEAT RESERVATION */}
        <section id="pricing-form" className="fy-section fy-section-alt">
          <div className="fy-container text-center">
            <div id="enrollment" className="py-2">
              <div className="fy-section-title-wrap text-center mb-3">
                <h2 className="fy-serif-heading" style={{ fontSize: '2rem' }}>
                  Course Enrollment &amp; Seat Reservation
                </h2>
                <div className="fy-title-underline"></div>
              </div>

              <p style={{ fontSize: '1.02rem', color: 'var(--fy-text-muted)', maxWidth: '880px', margin: '0 auto 36px', lineHeight: 1.75 }}>
                Our 500 Hour Master Yoga Teacher Training provides complete foundational and advanced mastery in one continuous journey. Due to our strict commitment to personal attention, cohort sizes are strictly limited.
              </p>

              <div className="fy-enrollment-cta-group">
                <button
                  type="button"
                  className="fy-btn-primary py-3 px-4"
                  style={{ borderRadius: '30px', fontWeight: 700, fontSize: '0.95rem', whiteSpace: 'nowrap' }}
                  onClick={() => setIsEnrollModalOpen(true)}
                >
                  Enroll Now &amp; Reserve Your Seat <i className="bi bi-arrow-right-circle-fill ms-1"></i>
                </button>

                <button
                  type="button"
                  className="fy-btn-secondary py-3 px-4"
                  style={{ borderRadius: '30px', fontWeight: 700, fontSize: '0.95rem', whiteSpace: 'nowrap' }}
                  onClick={() => openModal(courseTitle, dbCourse)}
                >
                  Enquire Now <i className="bi bi-chat-dots ms-1"></i>
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* 13. WHAT YOU RECEIVE */}
        <section className="fy-section">
          <div className="fy-container">
            <div className="fy-section-title-wrap">
              <span className="fy-eyebrow">EVERYTHING INCLUDED</span>
              <h2 className="fy-serif-heading">What You Receive</h2>
              <div className="fy-title-underline"></div>
              <p className="fy-hero-para mt-3" style={{ maxWidth: '650px', margin: '12px auto 0' }}>
                Everything included in the 500 Hour Yoga Teacher Training from the moment you enroll.
              </p>
            </div>

            <div className="fy-receive-grid">
              {highlights.map((item, idx) => (
                <div key={idx} className="fy-receive-card">
                  <div className="fy-receive-icon">
                    <i className="bi bi-check-lg"></i>
                  </div>
                  <span className="fy-receive-text">{item}</span>
                </div>
              ))}
            </div>

            <div className="fy-receive-assurance">
              <i className="bi bi-shield-check"></i>
              <span>All study manuals, live class links, sutra guides, and dashboard access are unlocked upon registration.</span>
            </div>
          </div>
        </section>

        {/* 14. CERTIFICATE SECTION */}
        <section className="fy-section fy-section-alt">
          <div className="fy-container">
            <div className="row align-items-center gy-4">
              <div className="col-lg-7">
                <span className="fy-eyebrow">ACADEMY CERTIFICATION</span>
                <h2 className="fy-serif-heading mb-3">500 Hour YTTC — Genuine & Verified Certification</h2>
                <p className="fy-hero-para mb-3">
                  Your <strong>500 Hour Yoga Teacher Training Certificate</strong> is issued through one of <strong>PranaFlow Academy’s verified Yoga Alliance-registered partner schools</strong>.
                </p>
                <p style={{ color: 'var(--fy-text-main)', fontSize: '0.95rem', lineHeight: '1.65', marginBottom: '1rem' }}>
                  Before entering into any partnership, we confirm the school’s <strong>Yoga Alliance registration status</strong> and evaluate its experience, teaching quality, faculty, and specialization. This allows us to work with the most suitable partner for each level of yoga teacher training.
                </p>
                <p style={{ color: 'var(--fy-text-main)', fontSize: '0.95rem', lineHeight: '1.65', marginBottom: '1rem' }}>
                  The partner school associated with your batch will be shared during your classes and can also be viewed in your student dashboard.
                </p>
                <p style={{ color: 'var(--fy-text-main)', fontSize: '0.95rem', lineHeight: '1.65', marginBottom: '1.5rem' }}>
                  After successfully completing the required training, attendance, practicum, assignments, and final assessment, you will receive a <strong>genuine certificate issued through the applicable verified Yoga Alliance-registered partner school</strong>.
                </p>

                <div className="fy-trust-badges mt-2 mb-2" style={{ display: 'inline-flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center', background: 'var(--fy-cream-card)', padding: '12px 20px', borderRadius: '30px', border: '1px solid var(--fy-sage-border)', fontSize: '0.88rem', fontWeight: '600', color: 'var(--fy-sage-dark)' }}>
                  <i className="bi bi-patch-check-fill text-success" style={{ fontSize: '1.1rem' }}></i>
                  <span>Yoga Alliance-Registered Partners · Verified Schools · Genuine Certification</span>
                </div>
              </div>

              <div className="col-lg-5 text-center">
                <div style={{ background: 'var(--fy-cream-card)', border: '2px solid var(--fy-sage-border)', borderRadius: 'var(--fy-radius-lg)', padding: '36px 28px', boxShadow: 'var(--fy-shadow-card)' }}>
                  <i className="bi bi-award-fill" style={{ fontSize: '3.8rem', color: 'var(--fy-sage-primary)' }}></i>
                  <h3 style={{ fontSize: '1.4rem', fontWeight: 800, color: 'var(--fy-sage-dark)', marginTop: '16px', marginBottom: '8px' }}>
                    500 Hour Yoga Teacher Certificate
                  </h3>
                  <div style={{ fontSize: '0.88rem', fontWeight: 600, color: 'var(--fy-sage-primary)', marginBottom: '16px' }}>Issued via Verified Yoga Alliance-Registered Partner School</div>
                  <p style={{ fontSize: '0.88rem', color: 'var(--fy-text-main)', lineHeight: 1.6, margin: 0 }}>
                    Awarded upon completing 500 hours of structured live online training, attendance, practicum, assignments, and final assessment through our verified partner school.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 15. FAQ */}
        <section id="faq" className="fy-section">
          <div className="fy-container">
            <div className="fy-section-title-wrap">
              <h2 className="fy-serif-heading">Frequently Asked Questions</h2>
              <div className="fy-title-underline"></div>
            </div>

            <div className="fy-faq-grid">
              {faqs.map((faq, idx) => (
                <div key={idx} className="fy-faq-item">
                  <button className="fy-faq-btn" aria-expanded={activeFaq === idx}
                    type="button"
                    onClick={() => setActiveFaq(activeFaq === idx ? null : idx)}>
                    <span>{faq.q}</span>
                    <div className="fy-faq-icon"><i className="bi bi-plus"></i></div>
                  </button>
                  {activeFaq === idx && (
                    <div className="fy-faq-ans" style={{ display: 'block' }}>
                      {faq.a}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 16. FINAL CTA BANNER */}
        <section className="fy-section fy-section-alt">
          <div className="fy-container">
            <div className="fy-cta-banner">
              <div className="row gy-4 align-items-center">
                <div className="col-lg-8">
                  <h2 className="fy-cta-heading">Don't Just Collect Another Yoga Certificate. Build the Knowledge Behind It.</h2>
                  <p className="fy-cta-sub">Go Beyond Practising Yoga. Learn to Understand It. Learn to Live It. Learn to Teach It.</p>
                  <p style={{ fontSize: '0.95rem', color: '#C3DFC8', lineHeight: 1.7, marginBottom: '20px' }}>
                    Master advanced asana alignment, Yoga Sutras, Bhagavad Gita philosophy, pranayama mechanics, functional anatomy, and live teaching practicum over 500 days with expert guidance managed in your PranaFlow Student Dashboard.
                  </p>
                  <div className="fy-btn-group">
                    <button type="button" className="fy-btn-primary fy-cta-btn-white" onClick={() => setIsEnrollModalOpen(true)}>
                      Enroll Now <i className="bi bi-arrow-right-circle-fill"></i>
                    </button>
                    <button type="button" className="fy-btn-secondary fy-cta-btn-outline" onClick={() => openModal(courseTitle, dbCourse)}>
                      Enquire Now
                    </button>
                  </div>
                </div>
                <div className="col-lg-4 text-center">
                  <div style={{ background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.25)', borderRadius: 'var(--fy-radius-lg)', padding: '24px 20px' }}>
                    <div className="fy-cta-logo-wrap">
                      <img 
                        src={getLogoUrl()} 
                        alt="PranaFlow Academy" 
                        className="fy-cta-card-logo"
                        onError={(e) => { e.target.onerror = null; e.target.src = '/apple-touch-icon.png'; }}
                      />
                    </div>
                    <div style={{ fontWeight: 800, fontSize: '1.1rem', color: '#ffffff', marginBottom: '4px' }}>500 Hours</div>
                    <div style={{ fontSize: '0.85rem', color: '#C3DFC8' }}>500 Training Days</div>
                    <div style={{ fontSize: '0.85rem', color: '#C3DFC8' }}>18 Detailed Modules</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 17. PROFESSIONAL SCOPE NOTICE */}
        <div className="fy-container mb-5">
          <div className="fy-page-disclaimer">
            <strong>Important Professional &amp; Health Notice:</strong> This course provides comprehensive master-level educational teacher training in traditional yoga, philosophy, pranayama, and teaching methodology. It does not qualify a graduate to practice clinical medicine, diagnose diseases, or provide medical treatment. Participants should practice mindfully within their personal physical limits.
          </div>
        </div>

      </main>

      <CourseEnrollModal
        isOpen={isEnrollModalOpen}
        onClose={() => setIsEnrollModalOpen(false)}
        courseTitle={courseTitle}
        formData={formData}
        setFormData={setFormData}
        upcomingBatches={upcomingBatches}
        isSubmitting={isSubmitting}
        isSuccess={isSuccess}
        handleEnrollSubmit={handleEnrollSubmit}
      />

      <PublicFooter />
    </div>
  );
};

export default YogaTTC500;
